import { SEO_COLORS } from 'cards/constants/Cards';
import _uniqBy from 'lodash/uniqBy';
import { getCapitalized } from 'paper/utils/helpers';
import { PLP_EVENTS_AND_TITLES } from 'cards/constants/CardTypes';
import { ALL_PAPER_SHAPES } from 'cards/constants/CardLevelOptions';
import { PARAM_KEY_TO__FILTER } from '../types';
import featureFlags from '../../../../util/featureFlags';
import { FilterKey, FilterValue } from './CardListingFilter/FilterTypes';
import { PARAM_CATEGORIES } from './constants';

export function getQueryFromFilter(
  key: keyof typeof PARAM_KEY_TO__FILTER,
  value: FilterValue,
  query?: Record<string, string>,
  urlKey?: string
): string {
  const paramKey = PARAM_KEY_TO__FILTER[key] || '';
  const queryKey = urlKey || key;
  let newQuery;

  if (query && query[queryKey]) {
    newQuery = value
      ? `${query[queryKey]},${paramKey}`
      : query[queryKey]
          .split(',')
          .filter(e => e !== paramKey)
          .join(',');
  } else {
    newQuery = paramKey;
  }
  return newQuery;
}

export function getFilter(
  filters: string[],
  onUpdateSearchFilters: (filterName: FilterKey, isFiltering: boolean) => void
): void {
  filters.forEach(filter => {
    const property = Object.keys(PARAM_KEY_TO__FILTER).find(
      each => filter === PARAM_KEY_TO__FILTER[each]
    );
    if (property) {
      onUpdateSearchFilters(property as FilterKey, true);
    }
  });
}

export const removeElementWithUuidDuplicates = <T extends { uuid: string }>(array: T[]): T[] =>
  _uniqBy(array, 'uuid');

export const getMotifsFromUrl = (urlFilter: string): string[] => {
  if (!urlFilter) return [];
  const activeStyles = urlFilter.split('--');
  const activeMotifFilterStyles = activeStyles
    .filter(e => !SEO_COLORS.includes(e.toUpperCase()) && !ALL_PAPER_SHAPES.includes(e))
    .map(e => getCapitalized(e));
  return activeMotifFilterStyles;
};

export const getSilhouettesFromUrl = (urlFilter: string): string[] => {
  if (!urlFilter) return [];

  const activeStyles = urlFilter.split('--');
  const activeSilhouettesFilterStyles = activeStyles
    .filter(e => ALL_PAPER_SHAPES.includes(e))
    .map(e => getCapitalized(e));
  return activeSilhouettesFilterStyles;
};

export const getSeoColorsFromUrl = (urlFilter: string): string[] => {
  if (!urlFilter || !featureFlags.get('seoColorSeasonFilters')) return [];
  const activeStyles = urlFilter.split('--');
  const activeSeoColorFilterStyles = activeStyles
    .filter(e => SEO_COLORS.includes(e.toUpperCase()))
    .map(e => e.toUpperCase());
  return activeSeoColorFilterStyles;
};

export const getFullSeoFiltersFromUrl = (urlFilter: string): string[] => {
  if (!urlFilter) return [];
  const activeStyles = urlFilter.split('--');
  const activeFilterStyles = featureFlags.get('seoColorSeasonFilters')
    ? activeStyles.map(e => e.toUpperCase())
    : activeStyles.filter(e => !SEO_COLORS.includes(e.toUpperCase())).map(e => getCapitalized(e));
  return activeFilterStyles;
};

export const getTitleCase = (text: string): string => {
  return text
    .split('_')
    .map((word: string) => word.slice(0, 1).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const isMultipleFilterOptions = (filterName: FilterKey, activeStyles: FilterValue): boolean => {
  let isMultipleFilters = false;

  if (Array.isArray(activeStyles)) {
    if (filterName === 'motifs') {
      isMultipleFilters =
        activeStyles.filter(
          e => !SEO_COLORS.includes(e.toUpperCase()) && !ALL_PAPER_SHAPES.includes(e)
        ).length > 1;
    } else if (filterName === 'silhouettes') {
      isMultipleFilters = activeStyles.filter(e => ALL_PAPER_SHAPES.includes(e)).length > 1;
    } else {
      isMultipleFilters = activeStyles.filter(e => SEO_COLORS.includes(e.toUpperCase())).length > 1;
    }
  }
  return isMultipleFilters;
};

const getFilterName = (filterOption: string): FilterKey =>
  (ALL_PAPER_SHAPES.includes(filterOption) && 'silhouettes') ||
  (SEO_COLORS.includes(filterOption.toUpperCase()) && 'multiColor') ||
  'motifs';

export const getHeroNamedStyle = (activeStyles: FilterValue): string => {
  let filterTitle = '';
  if (Array.isArray(activeStyles)) {
    filterTitle =
      activeStyles.find(e => !isMultipleFilterOptions(getFilterName(e), activeStyles)) || '';
  }

  return getTitleCase(filterTitle);
};

export const existenceCheck = (value: FilterValue): boolean => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return Boolean(value);
};

export const filterTransformation = (
  key: keyof typeof PARAM_KEY_TO__FILTER,
  value: FilterValue,
  query?: Record<string, string>,
  urlKey?: string
): string | null => {
  if (Array.isArray(value)) {
    return value.join(',').toLowerCase();
  }
  if (key === 'is_letterpress') {
    return value ? key : null;
  }

  return getQueryFromFilter(key, value, query, urlKey);
};

export const getPLPEventsAndTitles = (cardType: string, isDigital: boolean) => {
  if (isDigital) {
    return PLP_EVENTS_AND_TITLES['digital-save-the-date'];
  }
  return PLP_EVENTS_AND_TITLES[cardType as keyof typeof PLP_EVENTS_AND_TITLES];
};

export const getNewPathNameAndSearch = (
  newFilter: Record<string, any>,
  cardTypeUrl: string,
  isMultiSampleMode: boolean
): Record<string, string> => {
  const { motifs = [], silhouettes = [], seo_colors = [], ...otherValues } = newFilter;

  //* BUILD PATHNAME USING MOTIF, SHAPES AND COLOR FILTERS
  const styleValues = [];

  if (motifs) {
    styleValues.push(...(Array.isArray(motifs) ? motifs : [motifs]));
  }

  if (seo_colors) {
    styleValues.push(...(Array.isArray(seo_colors) ? seo_colors : [seo_colors]));
  }
  if (silhouettes) {
    styleValues.push(...(Array.isArray(silhouettes) ? silhouettes : [silhouettes]));
  }

  // @ts-ignore
  const activeStylesLower = motifs.map(style => style.toLowerCase());
  const sortedLowercaseStyleValues = styleValues
    .map(e => e.toLowerCase())
    .sort((a, b) => {
      const index1 = activeStylesLower.indexOf(a);
      const index2 = activeStylesLower.indexOf(b);
      return (index1 > -1 ? index1 : Infinity) - (index2 > -1 ? index2 : Infinity);
    });

  const stylesString = styleValues.length ? `/${sortedLowercaseStyleValues.join('--')}` : '';
  const newPathname = `/wedding-planning/${cardTypeUrl}/shop${stylesString}`;

  //* BUILD QUERY PARAMS USING ALL OTHER FILTERS
  // Go through all remaining filters, grouping all valid values by filter category.
  const filterValuesByCategory = {};
  Object.keys(otherValues).forEach(key => {
    // If the new filter value is falsy, it shouldn't be in the URL
    if (!otherValues[key]) {
      return;
    }

    // Only proceed if the filter is from a supported category
    const category = PARAM_CATEGORIES.find(c => c.keys.includes(key));
    if (category) {
      const value = filterTransformation(key, otherValues[key]);

      // Only proceed if the filter is set to a truthy value
      if (value) {
        const categoryKey = category.urlKey;
        // @ts-ignore
        const existingCategory = filterValuesByCategory[categoryKey];

        // Either add the value to an existing category or create a new one
        if (existingCategory) {
          existingCategory.push(value);
        } else {
          // @ts-ignore
          filterValuesByCategory[categoryKey] = [`${value}`];
        }
      }
    }
  });

  // Once all filters are split by category, stringify each individual category
  const searchValues = Object.keys(filterValuesByCategory).map(categoryKey => {
    // @ts-ignore
    const categoryValue = filterValuesByCategory[categoryKey].join(',');
    // @ts-ignore
    return `${categoryKey}=${categoryValue}`;
  });

  // Keep multi_sample_mode in the URL if currently enabled
  if (isMultiSampleMode) {
    searchValues.push('multi_sample_mode=multisampleenabled');
  }

  const newSearch = searchValues.length ? `?${searchValues.join('&')}` : '';

  return {
    newPathname,
    newSearch,
  };
};
